import React from 'react';
import { Row, TableState } from 'react-table';

import Select from '@alltrails/shared/denali/components/Select';

import FirstPage from '../icons/FirstPage';
import LastPage from '../icons/LastPage';
import PrevPage from '../icons/PrevPage';
import NextPage from '../icons/NextPage';

const rowHeight = 48;
const rowsPerPageOptions = [25, 50, 100, 500, 1000].map(rows => ({ label: `${rows}`, value: `${rows}` }));

type PaginationState = {
  styles: any;
  rows: Row[];
  page: Row[];
  pageOptions: number[];
  pageIndex: number;
  pageSize: number;
  gotoPage: (page: number) => void;
  previousPage: () => void;
  nextPage: () => void;
  setPageSize: (page: number) => void;
  pageCount: number;
  visibleColumnsCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
};

const initialPaginationState = (initialState: Partial<TableState>) => ({ pageSize: 25, ...initialState });

const renderPaginationFillerRow = ({ page, pageIndex, pageSize, visibleColumnsCount }: PaginationState) => {
  if (pageIndex > 0 && page.length < pageSize) {
    return (
      <tr>
        <td colSpan={visibleColumnsCount} style={{ height: (pageSize - page.length) * rowHeight }} />
      </tr>
    );
  }
  return null;
};

const renderPaginationControls = ({
  styles,
  rows,
  pageOptions,
  pageIndex,
  pageSize,
  gotoPage,
  previousPage,
  nextPage,
  setPageSize,
  pageCount,
  canPreviousPage,
  canNextPage
}: PaginationState) => {
  const from = pageIndex * pageSize + 1;
  const to = Math.min((1 + pageIndex) * pageSize, rows.length);
  const fromToOfRows = `${from}-${to} of ${rows.length} row${rows.length === 1 ? '' : 's'}`;
  const pageNoOptions = pageOptions.map((pageNo: number) => ({ label: `${pageNo + 1}`, value: `${pageNo}` }));

  return (
    <tfoot>
      <tr>
        <td>
          <span>Rows per page</span>
          <Select
            labelText=""
            hideLabel
            placeholder=""
            value={pageSize.toString()}
            options={rowsPerPageOptions}
            className={styles.rowsPerPageDropdown}
            size="sm"
            onChange={value => {
              setPageSize(Number(value));
            }}
            testId="rowsPerPageDropdown"
          />
          <span>{fromToOfRows}</span>
        </td>
        <td className={styles.pushRight}>
          <Select
            labelText=""
            hideLabel
            placeholder=""
            value={pageIndex.toString()}
            options={pageNoOptions}
            className={styles.pageNoDropdown}
            size="sm"
            onChange={value => {
              gotoPage(Number(value));
            }}
            testId="pageIndexDropdown"
          />
          <span>
            of {pageOptions.length} page{pageOptions.length === 1 ? '' : 's'}
          </span>
          <div className={styles.paginationButtons}>
            <button type="button" title="First page" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              <FirstPage />
            </button>
            <button type="button" title="Previous page" onClick={() => previousPage()} disabled={!canPreviousPage} data-testid="prev-page">
              <PrevPage />
            </button>
            <button type="button" title="Next page" onClick={() => nextPage()} disabled={!canNextPage} data-testid="next-page">
              <NextPage />
            </button>
            <button type="button" title="Last page" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              <LastPage />
            </button>
          </div>
        </td>
      </tr>
    </tfoot>
  );
};

export { PaginationState, initialPaginationState, renderPaginationFillerRow, renderPaginationControls };
